import { useCallback } from 'react';
import { useAtomCallback } from 'jotai/utils';
import { useLoadingBarState } from '../../../../../components/LoadingBar/LoadingBarContext';
import { useToastMessageState } from '../../../../../components/ToastMessage/ToastMessageProvider';
import {
  deleteTemplateAssignment,
  fetchTemplatesAssignments,
} from '../../../../../services/queries/KPITemplatesQueries';
import {
  kpiTemplateAssignmentsListStateJ,
  templateAssignmentsByCompanyIdStateJ,
} from '../../../../../services/state/KPI/KPICompaniesStateJ';
import { LoadingId } from '../../../../../types';
import { useInvalidateKPIRequests } from '../../KPIRequests/KPIRequestResponse/hooks/useInvalidateKPIRequests';

export function useClearTemplateAssignments() {
  const { actions } = useLoadingBarState();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const invalidateKpiRequests = useInvalidateKPIRequests();

  return useAtomCallback(
    useCallback(
      async (get, set, companyIds: number[]) => {
        const assignmentsByCompanyId = await get(templateAssignmentsByCompanyIdStateJ);

        const assignmentIds = companyIds.flatMap((companyId) => {
          const assignments = assignmentsByCompanyId.get(companyId) ?? [];
          return assignments.map((a) => a.id);
        });

        if (assignmentIds.length === 0) {
          return;
        }

        actions.startLoading(LoadingId.clearTemplateAssignments);
        try {
          const results = await Promise.allSettled(assignmentIds.map((id) => deleteTemplateAssignment(id)));

          const deletedCount = results.filter((r) => r.status === 'fulfilled').length;

          if (deletedCount > 0) {
            await invalidateKpiRequests();
            set(kpiTemplateAssignmentsListStateJ, await fetchTemplatesAssignments());
          }

          if (deletedCount === assignmentIds.length) {
            pushSuccessToast({ message: 'Template assignments cleared successfully' });
          } else if (deletedCount > 0) {
            pushErrorToast({
              message: `Cleared ${deletedCount} of ${assignmentIds.length} assignments. Some could not be removed.`,
            });
          } else {
            pushErrorToast({ message: 'Failed to clear template assignments' });
          }
        } catch (error) {
          console.error(error);
          pushErrorToast({ message: 'Failed to clear template assignments' });
        } finally {
          actions.stopLoading(LoadingId.clearTemplateAssignments);
        }
      },
      [actions, invalidateKpiRequests, pushErrorToast, pushSuccessToast]
    )
  );
}
